import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, Heart, Leaf, Sun, Users, Baby, CloudRain, Star } from "lucide-react";
import { useSEO, businessSchema } from "../lib/seo";
import { MaskLine, FadeIn, Reveal } from "../components/Reveal";
import { CTASection, Marquee } from "../components/Sections";
import { SERVICES } from "../data/services";
import { IMG, SITE } from "../lib/config";

const iconFor = { ansiedad: CloudRain, depresion: Sun, duelo: Heart, perinatal: Baby, infantil: Users };

const benefits = [
  { icon: Leaf, t: "Un espacio seguro", d: "Un lugar confidencial y sin juicios donde poder hablar de lo que de verdad te preocupa." },
  { icon: Heart, t: "Herramientas para tu vida", d: "No solo alivio: aprenderás recursos prácticos que podrás usar mucho más allá de la terapia." },
  { icon: Sun, t: "Cercanía y compromiso", d: "Un trato humano y cercano, adaptado siempre a tu ritmo y a tu momento, sin prisas." },
];

// Reseñas reales de pacientes (con su consentimiento).
const REVIEWS = [
  { name: "Chary", text: "Es muy atenta y por supuesto muy profesional, me está ayudando a gestionar muy bien la ansiedad que me provocan ciertas situaciones, es muy cercana y eso hace que todo sea más fácil. ¡Gracias!" },
  { name: "Noelia", text: "Gran profesional. Entusiasmo y entrega. Muy atenta desde el inicio del proceso. Explicaciones detalladas y con mucho compromiso conmigo desde la primera sesión." },
  { name: "Rocío R.", text: "Llevaba mucho tiempo queriendo ir a terapia pero siempre lo acababa posponiendo. Acudí a Victoria por recomendación y después de varias sesiones me arrepiento de no haber ido antes. Es muy cercana y empática, conecto muy bien con ella y me ayuda a superar mis miedos cada día. Te estoy infinitamente agradecida." },
];

export default function Home() {
  useSEO({
    title: "Psicóloga sanitaria en Málaga | Victoria Fuentes",
    description:
      "Victoria Fuentes, psicóloga sanitaria en Málaga. Atención psicológica para adultos, adolescentes y niños, presencial en Málaga y también online.",
    path: "/",
    image: IMG.hero,
    jsonLd: businessSchema,
  });

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scaleBg = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="grain relative flex min-h-screen items-end overflow-hidden">
        <motion.div style={{ y: yBg, scale: scaleBg }} className="absolute inset-0">
          <img src={IMG.hero} alt="Paisaje sereno de naturaleza al amanecer" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-ink/25" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/75 via-ink/15 to-ink/35" />
        </motion.div>

        <div className="relative mx-auto w-full max-w-7xl px-6 pb-20 pt-32 md:px-12 md:pb-28">
          <h1 className="mt-6 font-serif text-5xl font-medium leading-[1.05] tracking-tight text-bg-primary [text-shadow:0_2px_24px_rgba(0,0,0,0.4)] sm:text-6xl md:text-7xl lg:text-8xl">
            <MaskLine delay={0.35}>Victoria Fuentes,</MaskLine>
            <MaskLine delay={0.5}>psicóloga sanitaria</MaskLine>
            <MaskLine delay={0.65} className="italic text-sand">en Málaga</MaskLine>
          </h1>
          <FadeIn delay={0.8}>
            <p className="mt-5 font-serif text-2xl font-light italic text-white [text-shadow:0_2px_16px_rgba(0,0,0,0.45)] md:text-3xl">
              Te acompaño hacia tu bienestar emocional
            </p>
          </FadeIn>
          <FadeIn delay={0.9}>
            <p className="mt-8 max-w-xl text-lg font-medium leading-relaxed text-white [text-shadow:0_2px_16px_rgba(0,0,0,0.45)]">
              Un espacio seguro, cercano y sin juicios para comprender lo que te ocurre
              y avanzar hacia un mayor bienestar emocional.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contacto" data-testid="hero-cta-primary" className="inline-flex items-center gap-2 rounded-full bg-bg-primary px-8 py-4 text-sm font-medium text-ink transition-transform duration-300 hover:scale-[1.03]">
                Solicita tu primera cita <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
              </Link>
              <Link to="/sobre-mi" data-testid="hero-cta-secondary" className="inline-flex items-center gap-2 rounded-full border border-bg-primary/40 px-8 py-4 text-sm font-medium text-bg-primary transition-colors duration-300 hover:bg-bg-primary/10">
                Conóceme
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* INTRO / SERVICES */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
        <Reveal>
          <p className="overline">En qué puedo ayudarte</p>
          <h2 className="mt-4 max-w-3xl font-serif text-4xl font-light leading-tight text-ink md:text-5xl">
            Áreas en las que trabajo
          </h2>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-ink-soft">
            Cada persona y cada historia son únicas. Estas son las áreas en las que puedo acompañarte,
            siempre desde el respeto a tu ritmo.
          </p>
        </Reveal>

        <div className="mt-16 flex flex-wrap justify-center gap-6">
          {SERVICES.map((s, i) => {
            const Icon = iconFor[s.key];
            return (
              <Reveal key={s.slug} delay={i * 0.06} className="group w-full sm:w-[calc(50%-0.75rem)] lg:w-[calc(33.333%-1rem)]">
                <Link to={`/${s.slug}`} data-testid={`home-service-${s.key}`} className="block h-full overflow-hidden rounded-4xl border border-ink/5 bg-bg-secondary">
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img src={s.image} alt={s.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="flex items-start justify-between gap-4 p-7">
                    <div>
                      <Icon className="mb-4 h-6 w-6 text-sage-dark" strokeWidth={1.5} />
                      <h3 className="font-serif text-2xl text-ink">{s.name}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-ink-soft">{s.excerpt}</p>
                    </div>
                    <ArrowUpRight className="mt-1 h-5 w-5 shrink-0 text-ink-muted transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-sage-dark" strokeWidth={1.5} />
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      <Marquee />

      {/* BENEFITS */}
      <section className="bg-bg-secondary">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
          <Reveal>
            <p className="overline">Por qué ir a terapia</p>
            <h2 className="mt-4 max-w-2xl font-serif text-4xl font-light leading-tight text-ink md:text-5xl">
              No tienes que poder con todo. Pedir ayuda también es una forma de cuidarte.
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-10 md:grid-cols-3">
            {benefits.map((b, i) => (
              <Reveal key={b.t} delay={i * 0.08}>
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-bg-sage">
                  <b.icon className="h-6 w-6 text-sage-dark" strokeWidth={1.5} />
                </div>
                <h3 className="mt-6 font-serif text-2xl text-ink">{b.t}</h3>
                <p className="mt-3 text-base leading-relaxed text-ink-soft">{b.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
        <div className="grid items-center gap-12 md:grid-cols-12">
          <Reveal className="md:col-span-5">
            <div className="relative">
              <div className="overflow-hidden rounded-[2rem]">
                <img src={IMG.portrait} alt="Victoria Fuentes, psicóloga sanitaria en Málaga" loading="lazy" className="aspect-[4/5] w-full object-cover" />
              </div>
              <div className="absolute -bottom-5 -right-3 rounded-2xl bg-sage px-5 py-4 text-bg-primary shadow-lg md:-right-5">
                <p className="font-serif text-xl leading-none">Victoria Fuentes</p>
                <p className="mt-1 text-xs opacity-90">Colegiada nº {SITE.colegiada}</p>
              </div>
            </div>
          </Reveal>
          <div className="md:col-span-7 md:pl-6">
            <Reveal>
              <p className="overline">Sobre mí</p>
              <h2 className="mt-4 font-serif text-4xl font-light leading-tight text-ink md:text-5xl">
                Hola, soy Victoria
              </h2>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-ink-soft">
                Mi forma de trabajar parte de la escucha, la comprensión y el respeto por la situación particular de
                cada persona. Trabajo desde un enfoque integrador, adaptando las herramientas terapéuticas a lo que
                cada persona necesita, en un espacio seguro, confidencial y sin juicios.
              </p>
              <Link to="/sobre-mi" data-testid="home-about-link" className="mt-8 inline-flex items-center gap-2 rounded-full border border-ink/20 px-7 py-3.5 text-sm font-medium text-ink transition-colors duration-300 hover:bg-ink/5">
                Conóceme <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-bg-sage">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
          <Reveal>
            <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
              <div>
                <p className="overline">Opiniones</p>
                <h2 className="mt-4 max-w-2xl font-serif text-4xl font-light leading-tight text-ink md:text-5xl">
                  Historias de confianza
                </h2>
              </div>
              {SITE.doctoralia ? (
                <a href={SITE.doctoralia} target="_blank" rel="noopener noreferrer" data-testid="doctoralia-link" className="inline-flex shrink-0 items-center gap-2 rounded-full border border-ink/20 px-6 py-3 text-sm font-medium text-ink transition-colors duration-300 hover:bg-ink/5">
                  Ver más opiniones en Doctoralia <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
                </a>
              ) : null}
            </div>
          </Reveal>

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {REVIEWS.map((r, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <figure className="flex h-full flex-col rounded-4xl border border-ink/5 bg-bg-primary p-8">
                  <div className="flex gap-1 text-sand" aria-label="5 de 5 estrellas">
                    {[...Array(5)].map((_, s) => <Star key={s} className="h-4 w-4 fill-current" strokeWidth={0} />)}
                  </div>
                  <blockquote className="mt-5 flex-1 font-serif text-xl font-light italic leading-snug text-ink">
                    “{r.text}”
                  </blockquote>
                  <figcaption className="mt-6 text-sm font-medium text-ink-soft">
                    {r.name} <span className="font-normal text-ink-muted">· Opinión de paciente</span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HONORARIOS */}
      <section className="bg-bg-primary">
        <div className="mx-auto max-w-3xl px-6 py-24 text-center md:px-12 md:py-32">
          <Reveal>
            <h2 className="font-serif text-4xl font-light leading-tight text-ink md:text-5xl">Honorarios</h2>
            <div data-testid="honorarios-card" className="mx-auto mt-10 max-w-md rounded-4xl border border-ink/10 bg-bg-secondary p-8 md:p-10">
              <p className="font-serif text-2xl text-ink">Sesión individual</p>
              <p className="mt-4 font-serif text-5xl font-light leading-none text-sage-dark">60&nbsp;€</p>
              <p className="mt-4 text-sm text-ink-soft">Duración aproximada: 60 minutos.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <CTASection testId="home-cta" />
    </div>
  );
}
